import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools, persist } from "zustand/middleware";
import localforage from "localforage";
import { find, remove, unionBy } from "lodash";
import {
  CountEntryCard,
  CycleCountResults,
} from "../models/materialissue/MaterialIssue";
import { GetColumnsWithGroupResponse } from "../models/windows/GetColumnsWithGroup";

// Global (not per-batch) metadata the entry screen needs to render/validate
// offline. Cached once at download time, mirroring the WO hook's "download
// metadata once" section.
export type CycleCountMeta = {
  cardColumns?: GetColumnsWithGroupResponse; // mob_inventory_cyclecount
  scanColumns?: GetColumnsWithGroupResponse; // mob_inventory_cyclecount_scan
  scanOption?: string; // CycleCountingScannerConfiguration
  allowedVariance?: number; // AllowedCycleCountVariance
  adjustQtyCommentFlag?: boolean; // InventoryControl.AdjustQtyCommentFlag
};

type CycleCountState = {
  // batches selected in the "download for offline" UI (transient, not persisted)
  selectedBatches: CycleCountResults[];
  // batches that have been downloaded for offline access
  downloadedBatches: CycleCountResults[];
  // count items per batch, keyed by batch id
  countEntries: { [batchId: number]: CountEntryCard[] };
  // entry-screen metadata cached for offline rendering/validation
  meta: CycleCountMeta;
};

type CycleCountAction = {
  toggleSelectedBatch: (batch: CycleCountResults) => void;
  resetSelectedBatches: () => void;
  setDownloadedBatches: (items: CycleCountResults[]) => void;
  getDownloadedBatch: (batchId: number) => CycleCountResults | undefined;
  setCountEntriesForBatch: (
    batchId: number,
    entries: CountEntryCard[]
  ) => void;
  getCountEntriesForBatch: (batchId: number) => CountEntryCard[] | undefined;
  updateCountEntry: (batchId: number, entry: CountEntryCard) => void;
  setMeta: (meta: CycleCountMeta) => void;
  //reset
  resetAllButDownloadedBatches: () => void;
  reset: () => void;
};

const defaultState: CycleCountState = {
  selectedBatches: [],
  downloadedBatches: [],
  countEntries: {},
  meta: {},
};

export const useCycleCountStore = create<CycleCountState & CycleCountAction>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...defaultState,
        toggleSelectedBatch(batch) {
          set((state) => {
            const selected = [...state.selectedBatches];
            const existing = find(selected, {
              batch_id_0: batch.batch_id_0,
            });

            if (existing)
              remove(selected, {
                batch_id_0: batch.batch_id_0,
              });
            else selected.push(batch);
            state.selectedBatches = selected;
          });
        },
        resetSelectedBatches() {
          set((state) => {
            state.selectedBatches = [];
          });
        },
        setDownloadedBatches(items) {
          set((state) => {
            state.downloadedBatches = unionBy(
              items,
              [...state.downloadedBatches],
              (b) => b.batch_id_0
            );
          });
        },
        getDownloadedBatch(batchId) {
          return get().downloadedBatches.find(
            (b) => b.batch_id_0 === batchId
          );
        },
        setCountEntriesForBatch(batchId, entries) {
          set((state) => {
            state.countEntries = {
              ...state.countEntries,
              [batchId]: entries,
            };
          });
        },
        getCountEntriesForBatch(batchId) {
          return get().countEntries[batchId];
        },
        updateCountEntry(batchId, entry) {
          set((state) => {
            const entriesForBatch = state.countEntries[batchId] ?? [];
            state.countEntries = {
              ...state.countEntries,
              [batchId]: entriesForBatch.map((e) =>
                e.CountId === entry.CountId ? entry : e
              ),
            };
          });
        },
        setMeta(meta) {
          set((state) => {
            state.meta = { ...state.meta, ...meta };
          });
        },
        resetAllButDownloadedBatches() {
          set((state) => {
            const batchIdsToKeep = state.downloadedBatches.map(
              (b) => b.batch_id_0
            );
            state.selectedBatches = [];
            state.countEntries = Object.fromEntries(
              Object.entries(state.countEntries).filter(([key]) =>
                batchIdsToKeep.includes(Number(key))
              )
            );
          });
        },
        reset() {
          set(() => defaultState);
        },
      })),
      {
        name: "cycle-count",
        storage: {
          getItem: (key: string): Promise<any> => localforage.getItem(key),
          setItem: (key: string, value: any): Promise<void> =>
            localforage.setItem(key, value),
          removeItem: (key: string): Promise<void> =>
            localforage.removeItem(key),
        },
        partialize: (state) => ({
          downloadedBatches: state.downloadedBatches,
          countEntries: state.countEntries,
          meta: state.meta,
        }),
      }
    ),
    {
      name: "CycleCountStore",
      enabled: true,
    }
  )
);
